<script lang="ts">
  import Icon from '../icons/Icon.svelte';

  type Cell =
    | { kind: 'yes'; label?: string; note?: string }
    | { kind: 'no'; label?: string; note?: string }
    | { kind: 'mid'; label?: string; note?: string }
    | { kind: 'text'; label: string; note?: string };

  const rows: { feature: string; cells: [Cell, Cell, Cell, Cell, Cell] }[] = [
    {
      feature: 'Zero servers',
      cells: [
        { kind: 'yes', label: 'Yes' },
        { kind: 'no', label: 'Twilio' },
        { kind: 'no', label: 'Google' },
        { kind: 'yes', label: 'Yes' },
        { kind: 'no', label: 'Required', note: 'E2E, but account' }
      ]
    },
    {
      feature: 'Site detection',
      cells: [
        { kind: 'yes', label: 'Yes' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' }
      ]
    },
    {
      feature: 'Cross-device handoff',
      cells: [
        { kind: 'yes', label: 'QR · offline' },
        { kind: 'mid', label: 'Cloud sync' },
        { kind: 'mid', label: 'Cloud sync' },
        { kind: 'mid', label: 'Manual export' },
        { kind: 'yes', label: 'Yes' }
      ]
    },
    {
      feature: 'Code Teleport (15s buffer)',
      cells: [
        { kind: 'yes', label: 'Yes' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' },
        { kind: 'no', label: 'No' }
      ]
    },
    {
      feature: 'Open-source verifiable',
      cells: [
        { kind: 'yes', label: '+ reproducible', note: 'SRI hash pinned' },
        { kind: 'no', label: 'Closed' },
        { kind: 'no', label: 'Mostly closed' },
        { kind: 'yes', label: 'Yes' },
        { kind: 'yes', label: 'Yes' }
      ]
    },
    {
      feature: 'Account / phone number',
      cells: [
        { kind: 'yes', label: 'None', note: 'no signup' },
        { kind: 'no', label: 'Phone required' },
        { kind: 'mid', label: 'For sync' },
        { kind: 'yes', label: 'None' },
        { kind: 'no', label: 'Email required' }
      ]
    },
    {
      feature: 'Pricing',
      cells: [
        { kind: 'yes', label: 'Free · forever' },
        { kind: 'text', label: 'Free', note: 'data harvested' },
        { kind: 'text', label: 'Free', note: 'data harvested' },
        { kind: 'text', label: 'Free', note: 'donations' },
        { kind: 'text', label: 'Free / paid sync' }
      ]
    }
  ];

  const cols = ['VuAuth', 'Authy', 'Google Auth', 'Aegis', 'Ente Auth'];
</script>

<section id="compare" class="section scroll-reveal">
  <div class="wrap">
    <span class="eyebrow">Honest comparison</span>
    <h2 class="h-section heading">
      We built this knowing <em>everyone else's tradeoffs.</em>
    </h2>
    <p class="lede">
      Real comparisons against real alternatives. We won't claim to beat Signal at messaging — we
      won't claim to beat any of these on every axis either. Here's what's true.
    </p>

    <div class="cmp-wrap">
      <table class="cmp">
        <thead>
          <tr>
            <th scope="col" class="th-feature"><span class="visually-hidden">Feature</span></th>
            {#each cols as c, i (c)}
              <th scope="col" class:col-vu={i === 0}>{c}</th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#each rows as row (row.feature)}
            <tr>
              <td>{row.feature}</td>
              {#each row.cells as cell, i (i)}
                <td class:col-vu={i === 0}>
                  {#if cell.kind === 'yes'}
                    <span class="cmp-yes">
                      <Icon name="check" size={14} />
                      {cell.label ?? 'Yes'}
                    </span>
                  {:else if cell.kind === 'no'}
                    <span class="cmp-no">
                      <Icon name="x" size={14} />
                      {cell.label ?? 'No'}
                    </span>
                  {:else if cell.kind === 'mid'}
                    <span class="cmp-mid">
                      —
                      {cell.label ?? ''}
                    </span>
                  {:else}
                    {cell.label}
                  {/if}
                  {#if cell.note}<span class="note">{cell.note}</span>{/if}
                </td>
              {/each}
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <p class="cmp-note">
      <Icon name="info" size={14} />
      Aegis and Ente Auth are excellent — we'd use them if we hadn't built VuAuth. Their tradeoffs are
      the ones we set out to solve.
    </p>
  </div>
</section>

<style>
  .section {
    padding: 96px 0;
    position: relative;
  }
  @media (max-width: 640px) {
    .section {
      padding: 64px 0;
    }
  }
  :global([data-theme='brutalist']) .section {
    border-bottom: 2px solid var(--text);
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }
  .eyebrow {
    display: inline-flex;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    padding: 6px 12px;
    border: 1px solid var(--border-2);
    border-radius: 999px;
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .eyebrow {
    border-radius: 0;
    border: 2px solid var(--text);
    background: var(--accent-dim);
    color: var(--text);
    font-weight: 800;
  }
  .heading {
    margin-top: 18px;
    max-width: 780px;
  }
  .h-section {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .h-section em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .h-section {
    font-weight: 900;
  }
  :global([data-theme='brutalist']) .h-section em {
    color: var(--text);
  }
  .lede {
    margin-top: 18px;
    font-size: clamp(16px, 1.7vw, 20px);
    color: var(--text-2);
    line-height: 1.55;
  }
  .cmp-wrap {
    overflow-x: auto;
    margin-top: 48px;
    -webkit-overflow-scrolling: touch;
  }
  .cmp {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 720px;
  }
  .cmp th,
  .cmp td {
    padding: 16px 20px;
    text-align: left;
    vertical-align: middle;
  }
  .cmp thead th {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-3);
    border-bottom: 1px solid var(--border-2);
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .cmp thead th {
    border-bottom: 2px solid var(--text);
    background: var(--text);
    color: var(--bg);
    font-weight: 800;
  }
  .cmp tbody tr {
    border-bottom: 1px solid var(--border);
  }
  :global([data-theme='brutalist']) .cmp tbody tr {
    border-bottom: 1px solid var(--text);
  }
  .cmp tbody td {
    font-size: 14px;
  }
  .cmp tbody td:first-child {
    font-weight: 600;
    color: var(--text);
  }
  :global([data-theme='brutalist']) .cmp tbody td:first-child {
    font-weight: 800;
  }
  .col-vu {
    background: var(--accent-dim);
    position: relative;
  }
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .cmp-yes,
  .cmp-no,
  .cmp-mid {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 13px;
  }
  .cmp-yes {
    color: var(--good);
  }
  .cmp-no {
    color: var(--bad);
  }
  .cmp-mid {
    color: var(--warn);
  }
  :global([data-theme='brutalist']) .cmp-yes,
  :global([data-theme='brutalist']) .cmp-no,
  :global([data-theme='brutalist']) .cmp-mid {
    color: var(--text);
    font-weight: 800;
  }
  .note {
    display: block;
    font-size: 11px;
    color: var(--text-3);
    margin-top: 2px;
    font-weight: 400;
  }
  :global([data-theme='brutalist']) .note {
    color: var(--text);
  }
  .cmp-note {
    margin-top: 16px;
    font-size: 12px;
    color: var(--text-3);
    display: flex;
    align-items: center;
    gap: 8px;
  }
  :global([data-theme='brutalist']) .cmp-note {
    font-weight: 600;
    color: var(--text);
  }
</style>
